import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useUpdateOrganization } from '../../../entities/org';
import { identityErrorMessage } from '../../../shared/api/identity-error';
import { validationMessage } from '../../../shared/i18n/zod';
import { Button } from '../../../shared/ui/button';
import { Field } from '../../../shared/ui/field';
import { Input } from '../../../shared/ui/input';
import { toast } from '../../../shared/ui/sonner';

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, validationMessage('orgNameRequired'))
    .max(80, validationMessage('orgNameMax')),
});
type Values = z.infer<typeof schema>;

/** Renames the org (`org.manage`). The address does not change: slugs are stable. */
export function RenameOrgForm({ org }: { org: { id: string; name: string } }) {
  const { t } = useTranslation('org');
  const update = useUpdateOrganization(org.id);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<Values>({
    resolver: zodResolver(schema),
    values: { name: org.name },
  });
  return (
    <form
      className="flex flex-wrap items-start gap-3"
      onSubmit={handleSubmit(async ({ name }) => {
        setError(null);
        try {
          await update.mutateAsync(name);
          toast.success(t('settings.general.saved'));
        } catch (e) {
          setError(identityErrorMessage(e));
        }
      })}
    >
      <Field
        label={t('settings.general.name')}
        error={formState.errors.name?.message ?? error ?? undefined}
        className="min-w-0 flex-1 basis-64"
      >
        <Input id="org-name" autoComplete="organization" {...register('name')} />
      </Field>
      <Button
        type="submit"
        className="self-end sm:mt-7 sm:self-start"
        disabled={formState.isSubmitting}
      >
        {t('settings.general.save')}
      </Button>
    </form>
  );
}
